import { inject, Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { IApiErrorResponse } from '@core/models/api/api.models';
import {
  IMailboxTipoRuolo,
  IMailboxTipoRuoloCreateRequest,
  IMailboxTipoRuoloDeleteRequest,
  IMailboxTipoRuoloMailboxesMap,
  IMailboxTipoRuoloUpdateRequest
} from '@core/models/mailboxes.model';
import { ApiService } from '@core/services/api/api.service';
import { Console } from './logger.service';

/**
 * Servizio per la gestione dei ruoli associabili alle firme delle caselle mailbox.
 *
 * Operazioni disponibili:
 * - Elenco tipi ruolo
 * - Elenco mailbox con firma per tipo ruolo
 * - Creazione, aggiornamento e disattivazione (soft delete) di un tipo ruolo
 *
 * Tutte le chiamate utilizzano ApiService per la gestione HTTP e degli errori.
 *
 * **Standard Response API:**
 * - Risposte positive (HTTP 200): seguono lo standard `{ message: string, data?: T }`
 *   - Il campo `data` viene estratto automaticamente da ApiService
 * - Risposte di errore: seguono lo standard `IApiErrorResponse`
 *
 * @example
 * ```typescript
 * constructor(private mailboxesService: MailboxesService) {}
 *
 * this.mailboxesService.getTipoRuoli().subscribe({
 *   next: (ruoli) => console.log('Ruoli:', ruoli),
 *   error: (error) => console.error('Errore:', error)
 * });
 * ```
 */
@Injectable({
  providedIn: 'root'
})
export class MailboxesService {
  private readonly log_label = 'MailboxesService';
  private readonly api = inject(ApiService);

  /**
   * Recupera l'elenco di tutti i tipi ruolo mailbox disponibili.
   */
  getTipoRuoli(): Observable<IMailboxTipoRuolo[]> {
    const log_pref = this.log_label + '.getTipoRuoli() ';
    return this.api.get<IMailboxTipoRuolo[]>('mailbox/tipo-ruolo').pipe(
      catchError((error: IApiErrorResponse) => {
        const errorMessage = log_pref + (error?.message || 'Errore nel recupero dei tipi ruolo mailbox.');
        Console.log(errorMessage, error, 'err');
        return throwError(() => error);
      })
    );
  }

  /**
   * Recupera le mailbox la cui firma contiene il tipo ruolo indicato.
   *
   * @param idRuolo - ID del tipo ruolo
   * @returns Mappa email → nome visualizzato
   */
  getMailboxesByTipoRuolo(idRuolo: number): Observable<IMailboxTipoRuoloMailboxesMap> {
    const log_pref = this.log_label + '.getMailboxesByTipoRuolo() ';
    return this.api.get<IMailboxTipoRuoloMailboxesMap>(`mailbox/tipo-ruolo/${idRuolo}/mailboxes`).pipe(
      catchError((error: IApiErrorResponse) => {
        const errorMessage =
          log_pref + (error?.message || `Errore nel recupero delle mailbox per il tipo ruolo ${idRuolo}.`);
        Console.log(errorMessage, error, 'err');
        return throwError(() => error);
      })
    );
  }

  /**
   * Crea un nuovo tipo ruolo mailbox.
   */
  createTipoRuolo(payload: IMailboxTipoRuoloCreateRequest): Observable<string> {
    const log_pref = this.log_label + '.createTipoRuolo() ';
    return this.api.post<string, IMailboxTipoRuoloCreateRequest>('mailbox/tipo-ruolo', payload).pipe(
      catchError((error: IApiErrorResponse) => {
        const errorMessage = log_pref + (error?.message || 'Errore nella creazione del tipo ruolo mailbox.');
        Console.log(errorMessage, error, 'err');
        return throwError(() => error);
      })
    );
  }

  /**
   * Aggiorna la descrizione di un tipo ruolo mailbox, riattivandolo se era cancellato.
   */
  updateTipoRuolo(payload: IMailboxTipoRuoloUpdateRequest): Observable<string> {
    const log_pref = this.log_label + '.updateTipoRuolo() ';
    return this.api.patch<string, IMailboxTipoRuoloUpdateRequest>('mailbox/tipo-ruolo', payload).pipe(
      catchError((error: IApiErrorResponse) => {
        const errorMessage =
          log_pref + (error?.message || `Errore nell'aggiornamento del tipo ruolo ${payload.id_ruolo}.`);
        Console.log(errorMessage, error, 'err');
        return throwError(() => error);
      })
    );
  }

  /**
   * Disattiva un tipo ruolo mailbox (soft delete: imposta `cancellato` a true).
   */
  deleteTipoRuolo(idRuolo: number): Observable<string> {
    const log_pref = this.log_label + '.deleteTipoRuolo() ';
    const payload: IMailboxTipoRuoloDeleteRequest = { id_ruolo: idRuolo };
    return this.api.delete<string, IMailboxTipoRuoloDeleteRequest>('mailbox/tipo-ruolo', payload).pipe(
      catchError((error: IApiErrorResponse) => {
        const errorMessage =
          log_pref + (error?.message || `Errore nella disattivazione del tipo ruolo ${idRuolo}.`);
        Console.log(errorMessage, error, 'err');
        return throwError(() => error);
      })
    );
  }
}
